import { useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { BarChart, LineChart, Switch } from '@clarke-energia/foton-v2';

import eventTracker from '@services/event-tracking';
import { eventLabels } from '@services/event-tracking/events-label';

import { Contract } from '@hooks/get-contracts/types';
import ChartContainer from '@components/molecules/charts/chart-container';

import { discountChartDataFormatted, priceChartDataFormatted, volumeChartData } from './helpers';
import style from './style.module.css';

const ChartsSection = ({ contracts }: { contracts: Contract[] }) => {
  const [barUnit, setBarUnit] = useState<'MWH' | 'MWM'>('MWM');
  const priceList = contracts.filter((contract) => contract.prices?.length);
  const discountList = contracts.filter((contract) => contract.discount?.length);

  const barChart = volumeChartData(contracts, barUnit);
  const priceChart = useMemo(() => priceChartDataFormatted(priceList), [contracts]);
  const discountChart = useMemo(() => discountChartDataFormatted(discountList), [contracts]);

  return (
    <div className={twMerge(style.mainContainer, discountList.length ? style.minGridCols : style.defaultGridCols)}>
      <ChartContainer
        className={twMerge(style.chartContainer, discountList.length ? 'xl:col-span-3' : '')}
        title={{ text: 'Volume contratado ao longo dos anos' }}
        tooltipContent="Volume contratado médio mensal"
      >
        <div className={style.switchContainer}>
          <div className={style.switchLabelContainer}>
            MWh
            <span className="ml-3">
              <Switch
                onCheckedChange={(checked) => {
                  eventTracker.trackEvent(eventLabels.TOGGLE_VOLUME_CONTRACTS);
                  setBarUnit(checked ? 'MWM' : 'MWH');
                }}
                checked={barUnit === 'MWM'}
              />
            </span>
            MW-m
          </div>

          <BarChart
            title=""
            stack
            series={barChart.series}
            labels={barChart.labels}
            lines={[]}
            lineSeries={[]}
            options={{
              legend: { show: true },
              rightSpace: 0,
              leftSpace: 40,
              yAxisLabel: {
                decimalPlaces: 3,
              },
            }}
          />
        </div>
      </ChartContainer>

      <ChartContainer
        className={twMerge(style.chartContainer)}
        title={{ text: 'Preço de energia (R$/MWh) contratado ao longo dos anos' }}
      >
        <LineChart
          title=""
          series={priceChart.series}
          labels={priceChart.labels}
          lines={[]}
          options={{
            legend: { show: true },
            rightSpace: 0,
            leftSpace: 30,
            yAxisLabel: {
              decimalPlaces: 2,
            },
          }}
        />
      </ChartContainer>

      {discountList.length > 0 && (
        <ChartContainer
          className={twMerge(style.chartContainer, 'w-full')}
          title={{ text: 'Desconto de energia (%/MWh) contratado ao longo dos anos' }}
        >
          <LineChart
            title=""
            series={discountChart.series}
            labels={discountChart.labels}
            lines={[]}
            options={{
              legend: { show: true },
              rightSpace: 0,
              leftSpace: 30,
              yAxisLabel: {
                decimalPlaces: 2,
              },
            }}
          />
        </ChartContainer>
      )}
    </div>
  );
};

export default ChartsSection;
